import { gql } from '@apollo/client';
import { UPLOAD_FIELDS } from '../fragments';

/**
 * Omitting the cohort means "the cohorts I teach" — an administrator gets all
 * of them. `submissionCount` replaces fetching every submission to count them.
 */
export const GET_ALL_ACTIVITY = gql`
  query getAllActivities($cohortId: String) {
    activities(cohortId: $cohortId) {
      id
      title
      type
      hasTextInput
      instruction
      embedLink
      dueDate
      createdAt
      submissionCount
      cohort {
        id
        name
      }
      creator {
        id
        firstName
        lastName
      }
      subjects {
        id
        name
      }
      uploads {
        ...UploadFields
      }
    }
  }
  ${UPLOAD_FIELDS}
`;

export const GET_SINGLE_ACTIVITY = gql`
  query getActivity($id: String!) {
    activity(id: $id) {
      id
      title
      type
      hasTextInput
      embedLink
      instruction
      dueDate
      createdAt
      cohort {
        id
        name
      }
      subjects {
        id
        name
      }
      uploads {
        ...UploadFields
      }
    }
  }
  ${UPLOAD_FIELDS}
`;

/**
 * The caller's own work. The old query took a `where` filter and so could be
 * pointed at another learner's dashboard.
 */
export const GET_STUDENT_ACTIVITIES = gql`
  query getMyActivities {
    myActivities {
      id
      activityId
      userId
      status
      dueDate
      activity {
        id
        type
        title
        instruction
        hasTextInput
        dueDate
        embedLink
        cohort {
          id
          name
        }
        uploads {
          ...UploadFields
        }
      }
      submission {
        status
        rating
        textContent
        attachments {
          ...UploadFields
        }
      }
    }
  }
  ${UPLOAD_FIELDS}
`;

/** One activity of the caller's own, addressed by the activity id. */
export const GET_STUDENT_ACTIVITY = gql`
  query getMyActivity($activityId: String!) {
    myActivity(activityId: $activityId) {
      id
      activityId
      userId
      status
      dueDate
      activity {
        id
        title
        type
        instruction
        hasTextInput
        dueDate
        embedLink
        cohort {
          id
          name
        }
        uploads {
          ...UploadFields
        }
      }
      submission {
        status
        rating
        textContent
        attachments {
          ...UploadFields
        }
      }
    }
  }
  ${UPLOAD_FIELDS}
`;

/** The whole class's work for one activity — the grading list. */
export const GET_ACTIVITY_SUBMISSIONS = gql`
  query getActivitySubmissions($activityId: String!) {
    submissions(activityId: $activityId) {
      id
      userId
      status
      dueDate
      user {
        id
        firstName
        lastName
        profileStudent {
          admissionNumber
        }
      }
      submission {
        status
        rating
        textContent
        createdAt
      }
    }
  }
`;

/**
 * One learner's work. Submissions are embedded on the assignment record, so
 * they have no id of their own — the pair of activity and user identifies one.
 */
export const GET_ACTIVITY_SUBMISSION = gql`
  query getActivitySubmission($activityId: String!, $userId: String!) {
    submission(activityId: $activityId, userId: $userId) {
      id
      userId
      status
      dueDate
      activity {
        id
        title
      }
      user {
        id
        firstName
        lastName
        profileStudent {
          admissionNumber
        }
      }
      submission {
        status
        rating
        textContent
        createdAt
        attachments {
          ...UploadFields
        }
      }
    }
  }
  ${UPLOAD_FIELDS}
`;
